import React, { useEffect, useState, useCallback } from "react";
import { Box, Paper, Link, Typography, Grid } from "@mui/material";
import { useLocation, useParams, Link as RouterLink, data, useNavigate } from "react-router-dom";
import PrivateLayout from "../../layouts/privateLayout";
import BasicBreadcrumbs from "../../components/breadcrumb";
import { useApiClient } from "../../context/ApiClientContext";
import api from "../../helper/baseApi";
import Teams from "../../data/teamData";
import Form from "./form";
import ValueTagList from "./valueTagList";
import Members from "./members";
import ErrorAlert from "../../helper/errorAlert";
import EditTeamModal from "./editTeamModal";
import toast from "react-hot-toast";
import ComponentBackdrop from "../../components/backdrop";
import SuccessAlert from "../../helper/successAlert";

const Details = () => {
  const { id } = useParams();
  const location = useLocation();

  const [mode, setMode] = useState("view");
  const [dataList, setDataList] = useState({});
  const [members, setMembers] = useState([]);
  const [filteredDataList, setFilteredDataList] = useState([]);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openBackdrop, setOpenBackdrop] = useState(false);

  const [inputForm, setInputForm] = useState({
    name: "",
    teamPoint: "",
    startDate: "",
    endDate: "",
  });

  const {
    apiUrls: { mobileApiUrl },
  } = useApiClient();
  const navigate = useNavigate();

  const handleCloseModal = () => {
    setOpenEditModal(false);
  };

  useEffect(() => {
    const isViewMode = location.pathname.includes("view-team");
    setMode(isViewMode ? "view" : "create");

    if (isViewMode) {
      fetchData();

      // const selectedTeam = Teams.find((team) => team.id === id);

      // if (selectedTeam) {
      //   setDataList(selectedTeam);
      // } else {
      //   console.warn("Team not found for id:", id);
      // }
    }
  }, [location.pathname, id]);

  const fetchData = async (page) => {
    try {
      const apiUrl = `${mobileApiUrl}team/view?teamId=${id}`;
      const response = await api.get(apiUrl);

      const teamItem = response?.data?.data || [];
      console.log('teamItem', teamItem);

      setDataList(teamItem);
    } catch (error) {
      ErrorAlert(error, navigate);
    }
  };

  const fetchUserList = useCallback(async () => {
    try {
      const apiUrl = `${mobileApiUrl}user/user-employee-list`;
      const response = await api.get(apiUrl);
      const users = response.data.data.userEmployeeList || [];
      console.log('users', users);

      const formattedUsers = users.map((item) => ({
        ...item,
        id: item.employeeId,
      }));

      setFilteredDataList(formattedUsers);
    } catch (error) {
      console.error("Error fetching user list:", error?.response?.statusText);
      setFilteredDataList([]);
    }
  }, [mobileApiUrl]);

  useEffect(() => {
    fetchUserList();
  }, [fetchUserList]);

  useEffect(() => {
    if (!dataList?.teamMembers?.length || !filteredDataList.length) return;

    const matchedMembers = dataList.teamMembers.map((member, index) => {
      const user = filteredDataList.find(
        (item) =>
          String(item.employeeId) === String(member.employeeId) ||
          String(item.code) === String(member.employeeId)
      );

      return {
        // keep all employee info if found
        ...user,
        id: user?.id,
        // always include teamMemberId from the original teamMembers
        teamMemberId: member.teamMemberId,
        // fallback id for DataGrid row if user not found
        employeeId: user?.id || member.employeeId,
        // fallback name if user not found
        name: user?.name || "Unknown",
      };
    });
    console.log("matchedMembers", matchedMembers);
    setMembers(matchedMembers);
  }, [dataList, filteredDataList]);

  const handleInputChange = (field, e) => {
    let value = e.target.value;

    if (field === "teamPoint") {
      value = value === "" ? "" : Number(value); // convert to number
    }

    setInputForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    setOpenBackdrop(true);
    try {
      const payload = {
        teamId: dataList.teamId,
        name: inputForm.name,
        code: dataList.code,
        teamPoint: inputForm.teamPoint,
        startDate: inputForm.startDate,
        endDate: inputForm.endDate,
        isActive: dataList.isActive,
      };

      const response = await api.put(`${mobileApiUrl}team/update-team`, payload);

      if (response.status === 200) {
        await fetchData();

        setTimeout(() => {
          SuccessAlert(response.data.message);
          setOpenEditModal(false);
          setOpenBackdrop(false);
        }, 2000); // 500ms delay
      } else {
        toast.error("Failed to add team members. Please try again.");
      }
    } catch (error) {
      ErrorAlert(error, navigate);
      setTimeout(() => {        
      setOpenEditModal(false);
      setOpenBackdrop(false);
      }, 500);
    }
  };

  const labelWidth = 80;

  const formatDate = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    if (isNaN(d)) return "-";
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const renderField = (label, id) => (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        gap: 1,
      }}
    >
      <Typography
        sx={{
          minWidth: { sm: labelWidth },
          alignItems: { sm: "center" },
          fontSize: 14,
        }}
      >
        {label}
      </Typography>
      <Typography>
        :
      </Typography>
      <Typography sx={{ fontSize: 14, }}>
        {id}
      </Typography>
    </Box>
  );

  return (
    <PrivateLayout>
      <Box sx={{ mx: "40px", mt: 5, mb: 5 }}>
        {/* Breadcrumb Header */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            gap: 2,
            mb: 3,
          }}
        >
          <BasicBreadcrumbs
            first={
              <Link
                component={RouterLink}
                to="/team"
                underline="hover"
                color="primary.main"
              >
                Teams
              </Link>
            }
            second={
              mode === "view"
                ? "View"
                : mode === "edit"
                  ? "Edit"
                  : "Add"
            }
          />

          {mode === "view" && (
            <Box>
              <Box
                onClick={() => {
                  setInputForm({
                    name: dataList.name,
                    teamPoint: dataList.teamPoint,
                    startDate: dataList.startDate,
                    endDate: dataList.endDate,
                  });
                  setOpenEditModal(true);
                }}
                sx={{
                  px: 3,
                  py: 1,
                  mx: 5,
                  borderRadius: "5px",
                  backgroundColor: "secondary.main",
                  color: "#fff",
                  fontSize: 14,
                  fontWeight: 500,
                  cursor: "pointer",
                  "&:hover": { backgroundColor: "secondary.dark" },
                }}
              >
                Edit
              </Box>
            </Box>
          )}
        </Box>

        {/* Page Content */}
        <Box px={3}>
          <Grid container columnSpacing={{ xs: 1, sm: 2, md: 4 }} width="100%">
            <Grid size={{ xs: 12, md: 3 }}>
              {renderField('Name ', dataList.name)}
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              {renderField('Point', dataList.teamPoint)}
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              {renderField('Start Date', formatDate(dataList.startDate))}
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              {renderField('End Date', formatDate(dataList.endDate))}
            </Grid>
          </Grid>

          <ValueTagList valueTagSet={dataList.teamValueTags} teamID={dataList.teamId}  refreshData={fetchData}/>

          <Members teamMembers={members} teamID={dataList.teamId} refreshTeam={fetchData} />
        </Box>

        {/* ✅ Edit Modal */}
        <EditTeamModal
          open={openEditModal}
          onClose={handleCloseModal}
          inputForm={inputForm}
          handleInputChange={handleInputChange}
          onSubmit={handleSave}
        />

      </Box>
      <ComponentBackdrop openBackdrop={openBackdrop} />

    </PrivateLayout>
  );
};

export default Details;
